import * as React from 'react';
import { Link } from 'react-router-dom';
import { OrderInfo, ProductApi, Category, OrderInfoDelivery } from 'common/types';
import { Grid } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { RentalsTabValue } from 'services/types';
import classNames from 'classnames';
import { RentalsTabValues } from 'services/types';
import RentalCardStatus from './RentalCardStatus';
import RentalCardBookingStatus from './RentalCardBookingStatus';
import RentalCardShoppers from './RentalCardShoppers';
import RentalCardLocations from './RentalCardLocations';
import RentalCardChannelBar from './RentalCardChannelBar';
import RentalCardProducts from './RentalCardProducts';
import RentalCardStatusBar from './RentalCardStatusBar';

interface Props {
	rentalInfo: OrderInfo;
	rentalTab: RentalsTabValue;
	onClick?: (rentalId: string) => void;
	showLocations: boolean;
	dateExpired?: boolean;
	stockProducts?: ProductApi[];
	categories?: Category[];
	dataQA?: string;
	linkTo?: string;
	delivery?: OrderInfoDelivery;
}

export const RentalCard = (props: Props) => {
	const { rentalInfo, rentalTab, delivery } = props;
	const classes = useStyles();

	const content = (
		<Card
			key={rentalInfo.id}
			className={classNames(
				classes.rentalCard,
				rentalTab === RentalsTabValues.COMPLETED && classes.rentalCardCompleted,
			)}
			onClick={() => props.onClick?.(rentalInfo.id)}
			elevation={0}
		>
			{rentalTab === RentalsTabValues.BOOKED && <RentalCardChannelBar rentalInfo={rentalInfo} />}
			<CardContent classes={{ root: classes.cardContent }}>
				<Grid container alignItems="flex-start" className={classes.contentWrapper} spacing={1}>
					<Grid item xs={8} sm={9} md={10} container spacing={1}>
						<Grid item xs={12} sm={7} md={5}>
							<RentalCardShoppers rentalInfo={rentalInfo} dataQA={props.dataQA} />
							<RentalCardBookingStatus rentalInfo={rentalInfo} />
						</Grid>
						{props.stockProducts && props.categories && (
							<Grid item container xs={12} sm={5} md={7}>
								<Grid item xs={12}>
									<RentalCardProducts stockProducts={props.stockProducts} rentalInfo={rentalInfo} />
								</Grid>
							</Grid>
						)}
					</Grid>
					<Grid item xs={4} sm={3} md={2}>
						<RentalCardStatus
							rentalTab={rentalTab}
							rentalInfo={rentalInfo}
							dateExpired={props.dateExpired}
							delivery={delivery}
						/>
					</Grid>
				</Grid>
				{props.showLocations && (
					<RentalCardLocations
						rentalInfo={rentalInfo}
						rentalTab={rentalTab}
						showLocations={props.showLocations}
					/>
				)}
				<RentalCardStatusBar rentalInfo={rentalInfo} rentalTab={rentalTab} delivery={delivery} />
			</CardContent>
		</Card>
	);

	return props.linkTo ? <Link to={props.linkTo}>{content}</Link> : content;
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		cardContent: {
			paddingLeft: theme.spacing(4),
			minHeight: 180,
			display: 'flex',
			flexDirection: 'column',
		},
		contentWrapper: {
			flex: 1,
			[theme.breakpoints.down('md')]: {
				marginBottom: theme.spacing(2),
			},
		},
		rentalCard: {
			position: 'relative',
			borderBottom: `2px solid ${theme.palette.border.paper}`,
			borderRadius: theme.spacing(0.5),
			cursor: 'pointer',
			boxShadow: '0 2px 6px 0 rgba(163, 163, 163, 0.5)',
		},
		rentalCardCompleted: {
			margin: theme.spacing(1, 0),
		},
	}),
);

export default RentalCard;
